import type { Lang } from "./api";

const STR = {
  title: { en: "VOX Cinemas", ar: "فوكس سينما" },
  subtitle: { en: "Virtual Assistant", ar: "المساعد الافتراضي" },
  placeholder: { en: "Movie night? Tell me what you're in the mood for…", ar: "ليلة سينما؟ قل لي ما الذي تشتهيه…" },
  startVoice: { en: "Let's talk movies", ar: "لنتحدث عن الأفلام" },
  startText: { en: "I'd rather type", ar: "أفضّل الكتابة" },
  connecting: { en: "Connecting…", ar: "جارٍ الاتصال…" },
  listening: { en: "Listening", ar: "أستمع" },
  speaking: { en: "Speaking", ar: "يتحدث" },
  mute: { en: "Mute", ar: "كتم" },
  unmute: { en: "Unmute", ar: "إلغاء الكتم" },
  end: { en: "End", ar: "إنهاء" },
  send: { en: "Go", ar: "أرسل" },
  switchToVoice: { en: "Say it instead", ar: "قلها بصوتك" },
  switchToText: { en: "Type it instead", ar: "اكتبها بدلاً من ذلك" },
  you: { en: "You", ar: "أنت" },
  agent: { en: "VOX Cinemas", ar: "فوكس سينما" },
  human: { en: "Customer Care", ar: "خدمة العملاء" },
  showtimes: { en: "Showtimes", ar: "مواعيد العرض" },
  trailer: { en: "Trailer", ar: "الإعلان" },
  book: { en: "Book", ar: "احجز" },
  seats: { en: "Seats", ar: "المقاعد" },
  total: { en: "Total", ar: "الإجمالي" },
  pay: { en: "Pay now", ar: "ادفع الآن" },
  screen: { en: "SCREEN", ar: "الشاشة" },
  available: { en: "Available", ar: "متاح" },
  selected: { en: "Selected", ar: "مختار" },
  taken: { en: "Taken", ar: "محجوز" },
  confirmSeats: { en: "Confirm seats", ar: "تأكيد المقاعد" },
  cardNumber: { en: "Card number", ar: "رقم البطاقة" },
  expiry: { en: "MM/YY", ar: "الشهر/السنة" },
  cvv: { en: "CVV", ar: "CVV" },
  payButton: { en: "Pay", ar: "ادفع" },
  processing: { en: "Processing…", ar: "جارٍ المعالجة…" },
  bookingRef: { en: "Booking reference", ar: "الرقم المرجعي" },
  scanQr: { en: "Scan this QR at the entrance", ar: "امسح رمز QR عند المدخل" },
  rateUs: { en: "How did we do?", ar: "كيف كان أداؤنا؟" },
  thanks: { en: "Thanks for your feedback!", ar: "شكراً لملاحظاتك!" },
  comment: { en: "Any comment? (optional)", ar: "أي تعليق؟ (اختياري)" },
  submit: { en: "Submit", ar: "إرسال" },
  transferring: { en: "Connecting you to Customer Care…", ar: "جارٍ توصيلك بخدمة العملاء…" },
  connectedTo: { en: "You're chatting with", ar: "تتحدث الآن مع" },
  transferEnded: { en: "The agent has left the chat.", ar: "غادر الموظف المحادثة." },
  eligible: { en: "Refundable", ar: "قابل للاسترداد" },
  notEligible: { en: "Not refundable", ar: "غير قابل للاسترداد" },
  refund: { en: "Refund", ar: "الاسترداد" },
  km: { en: "km away", ar: "كم" },
  directions: { en: "Directions", ar: "الاتجاهات" },
  parking: { en: "Parking", ar: "المواقف" },
  openMaps: { en: "Open in Maps", ar: "افتح الخريطة" },
  micDenied: { en: "Microphone access was denied — switching to text.", ar: "تم رفض الوصول إلى الميكروفون — التحويل إلى النص." },
  demoHint: { en: "Demo tips", ar: "نصائح العرض" },
  minimise: { en: "Minimise", ar: "تصغير" },
  askVoxi: { en: "Make it a movie night", ar: "ليلتك تبدأ بفيلم" },
  thinking: { en: "Thinking…", ar: "جارٍ التفكير…" },
  tryAsking: { en: "Try one of these", ar: "جرّب واحدة من هذه" },
  online: { en: "Online", ar: "متصل" },
  tapToTalk: { en: "Tap the mic and just say it — or type below", ar: "اضغط الميكروفون وقلها — أو اكتب أدناه" },
  moreTimes: { en: "more", ar: "المزيد" },
  today: { en: "Today", ar: "اليوم" },
  secure: { en: "Secure payment · card details never leave your browser", ar: "دفع آمن · بيانات البطاقة لا تغادر متصفحك" },
  admitOne: { en: "Admit", ar: "دخول" },
} as const;

export type Key = keyof typeof STR;
export const t = (lang: Lang, k: Key) => STR[k][lang];
export const isRtl = (lang: Lang) => lang === "ar";
export function paymentMethodLabel(method: unknown, lang: Lang): string {
  const labels: Record<string, [string, string]> = { VOX_CREDIT: ["VOX Credit", "رصيد فوكس"], SHARE_POINTS: ["SHARE Points", "نقاط شير"], ORIGINAL_PAYMENT: ["Original payment method", "طريقة الدفع الأصلية"], CARD: ["Original card", "البطاقة الأصلية"], SAVED_CARD: ["Saved card", "البطاقة المحفوظة"] };
  return labels[String(method)]?.[lang === "ar" ? 1 : 0] ?? (lang === "ar" ? "طريقة غير محددة" : "Method not specified");
}
export const money = (cents: number, lang: Lang) => (lang === "ar" ? `${(cents / 100).toFixed(cents % 100 ? 2 : 0)} د.إ` : `AED ${(cents / 100).toFixed(cents % 100 ? 2 : 0)}`);
